import Vue from 'vue';
import VueRouter from 'vue-router';

import store from '@/store';
import {
  redirectAuthenticatedUsers,
  requireEmailVerification,
  requireSubscription,
  requirePayment,
  multiguard,
} from './guards';

Vue.use(VueRouter);

const Login = () => import('@/views/Login.vue');
const Register = () => import('@/views/Register.vue');
const RecoverPassword = () => import('@/views/RecoverPassword.vue');
const EmailVerification = () => import('@/views/EmailVerification.vue');
const Dashboard = () => import('@/views/Dashboard.vue');
const NewEstimate = () => import('@/views/NewEstimate.vue');
const PastEstimates = () => import('@/views/PastEstimates.vue');
const EditEstimate = () => import('@/views/EditEstimate.vue');
const Settings = () => import('@/views/Settings.vue');
const Checkout = () => import('@/views/Checkout.vue');

const TimeModifications = () => import('@/components/settings/TimeModifications.vue');
const Subscription = () => import('@/components/settings/Subscription.vue');

const routes = [
  {
    path: '/',
    redirect: '/login',
  },
  {
    path: '/register',
    name: 'register',
    component: Register,
    beforeEnter: redirectAuthenticatedUsers,
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    beforeEnter: redirectAuthenticatedUsers,
  },
  {
    path: '/recover-password',
    name: 'recoverPassword',
    component: RecoverPassword,
    beforeEnter: redirectAuthenticatedUsers,
  },
  {
    path: '/email-verification',
    name: 'emailVerification',
    component: EmailVerification,
    beforeEnter: (to, from, next) => {
      const { loggedIn: userLoggedIn, details } = store.state.user;

      if (!userLoggedIn) return next('/login');
      if (details && details.emailVerified) return next('/dashboard');
      return next();
    },
  },
  {
    path: '/checkout',
    name: 'checkout',
    component: Checkout,
    beforeEnter: requireEmailVerification,
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: Dashboard,
    beforeEnter: multiguard([requireEmailVerification, requireSubscription]),
  },
  {
    path: '/new-estimate',
    name: 'newEstimate',
    component: NewEstimate,
    beforeEnter: multiguard([requireEmailVerification, requireSubscription, requirePayment]),
  },
  {
    path: '/past-estimates',
    name: 'pastEstimates',
    component: PastEstimates,
    beforeEnter: multiguard([requireEmailVerification, requireSubscription, requirePayment]),
  },
  {
    path: '/edit-estimate/:id',
    name: 'editEstimate',
    component: EditEstimate,
    beforeEnter: multiguard([
      requireEmailVerification,
      requireSubscription,
      requirePayment,
      (to, from, next) => {
        store.dispatch('estimate/loadEstimate', { id: to.params.id })
          .then(next);
      },
      (to, from, next) => {
        store.dispatch('estimate/getSettingsForEstimation', {
          id: store.state.estimate.current.data.settingsId,
        }).then(next);
      },
    ]),
  },
  {
    path: '/settings',
    component: Settings,
    beforeEnter: multiguard([requireEmailVerification, requireSubscription, requirePayment]),
    children: [
      {
        path: '/',
        name: 'settings-time',
        component: TimeModifications,
      },
      {
        path: 'subscription',
        name: 'settings-subscription',
        component: Subscription,
      },
    ],
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  const { loggedIn: userLoggedIn } = store.state.user;

  return !['register', 'login', 'recoverPassword'].includes(to.name) && !userLoggedIn
    ? next('/login')
    : next();
});

export default router;
